import { RefreshCcw, Search, Send } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { contactService, type ContactMessage } from '../../../shared/services/contactService';
import { notificationService } from '../../../shared/services';

const formatTimestamp = (value?: string): string => {
  if (!value) return '';
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return value;
  return date.toLocaleString();
};

type LocalReply = {
  id: string;
  body: string;
  createdAt: string;
};

type MessageListItem = ContactMessage & {
  selectionKey: string;
};

export default function AdminContactMessagesPage() {
  const [messages, setMessages] = useState<ContactMessage[]>([]);
  const [activeSelectionKey, setActiveSelectionKey] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [replyDraft, setReplyDraft] = useState('');
  const [repliesByMessageId, setRepliesByMessageId] = useState<Record<string, LocalReply[]>>({});
  const [isReplySending, setIsReplySending] = useState(false);
  const [replyFeedback, setReplyFeedback] = useState('');

  const loadMessages = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const data = await contactService.getAll();
      const sorted = [...data].sort((left, right) => {
        const leftTime = left.createdAt ? new Date(left.createdAt).getTime() : 0;
        const rightTime = right.createdAt ? new Date(right.createdAt).getTime() : 0;
        return rightTime - leftTime;
      });
      setMessages(sorted);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Unable to load contact messages.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadMessages();
  }, [loadMessages]);

  const filtered = useMemo<MessageListItem[]>(() => {
    const normalized = query.trim().toLowerCase();
    const visibleMessages = !normalized
      ? messages
      : messages.filter((message) => {
          const haystack = `${message.name} ${message.email} ${message.subject} ${message.message}`.toLowerCase();
          return haystack.includes(normalized);
        });

    return visibleMessages.map((message, index) => ({
      ...message,
      selectionKey: [
        message.id,
        message.createdAt || 'no-date',
        message.email || 'no-email',
        index,
      ].join('::'),
    }));
  }, [messages, query]);

  const activeMessage = useMemo(
    () => filtered.find((message) => message.selectionKey === activeSelectionKey) ?? filtered[0] ?? null,
    [activeSelectionKey, filtered],
  );

  useEffect(() => {
    if (!filtered.length) {
      if (activeSelectionKey !== null) {
        setActiveSelectionKey(null);
      }
      return;
    }

    const selectedStillVisible = filtered.some((message) => message.selectionKey === activeSelectionKey);
    if (!selectedStillVisible) {
      setActiveSelectionKey(filtered[0].selectionKey);
    }
  }, [activeSelectionKey, filtered]);

  useEffect(() => {
    setReplyDraft('');
    setReplyFeedback('');
  }, [activeMessage?.id]);

  const activeReplies = useMemo(() => {
    if (!activeMessage) return [];
    return repliesByMessageId[activeMessage.id] ?? [];
  }, [activeMessage, repliesByMessageId]);

  const handleSendReply = useCallback(async () => {
    if (!activeMessage) return;
    const text = replyDraft.trim();
    if (!text) return;
    if (!activeMessage.email?.trim()) {
      setReplyFeedback('This message has no email address.');
      return;
    }

    const reply: LocalReply = {
      id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
      body: text,
      createdAt: new Date().toISOString(),
    };

    try {
      setIsReplySending(true);
      setReplyFeedback('');

      const subjectPrefix = activeMessage.subject?.trim() ? `Re: ${activeMessage.subject.trim()}\n\n` : '';
      const originalMeta = `\n\n---\nOriginal message (${formatTimestamp(activeMessage.createdAt)}):\n${activeMessage.message || ''}`;
      await notificationService.create({
        sendTo: activeMessage.email.trim(),
        notificationBody: `${subjectPrefix}${text}${originalMeta}`.trim(),
      });

      setRepliesByMessageId((prev) => ({
        ...prev,
        [activeMessage.id]: [...(prev[activeMessage.id] ?? []), reply],
      }));
      setReplyDraft('');
      setReplyFeedback('Reply sent to buyer notifications (and email if enabled).');
    } catch (err: unknown) {
      setReplyFeedback(err instanceof Error ? err.message : 'Unable to send reply.');
    } finally {
      setIsReplySending(false);
    }
  }, [activeMessage, replyDraft]);

  return (
    <section className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm sm:p-6">
      <div className="grid gap-4 lg:grid-cols-[360px_1fr]">
        <aside className="rounded-3xl border border-slate-200 bg-slate-50 p-3">
          <div className="flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2">
            <Search className="h-4 w-4 text-slate-500" />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search name, email, subject…"
              className="w-full bg-transparent text-sm text-slate-900 outline-none placeholder:text-slate-400"
            />
          </div>

          <div className="mt-2 flex justify-end">
            <button
              type="button"
              onClick={() => void loadMessages()}
              disabled={isLoading}
              className={`inline-flex items-center justify-center gap-2 rounded-2xl border px-3 py-2 text-xs font-semibold transition ${
                isLoading
                  ? 'cursor-not-allowed border-slate-200 bg-white text-slate-400'
                  : 'border-orange-200 bg-white text-orange-700 hover:bg-orange-50'
              }`}
            >
              <RefreshCcw className="h-4 w-4" />
              Refresh
            </button>
          </div>

          {error && (
            <div className="mt-3 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-700">
              {error}
            </div>
          )}

          <div className="mt-3 max-h-[560px] space-y-2 overflow-y-auto pr-1">
            {isLoading && messages.length === 0 && (
              <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600">Loading…</div>
            )}

            {!isLoading && filtered.length === 0 && (
              <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600">
                No messages found.
              </div>
            )}

            {filtered.map((message) => {
              const isActive = activeMessage?.selectionKey === message.selectionKey;
              return (
                <button
                  key={message.selectionKey}
                  type="button"
                  onClick={() => setActiveSelectionKey(message.selectionKey)}
                  aria-pressed={isActive}
                  className={`w-full rounded-2xl border px-4 py-3 text-left transition ${
                    isActive
                      ? 'border-orange-200 bg-white shadow-sm'
                      : 'border-slate-200 bg-white hover:border-orange-200 hover:bg-orange-50/30'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-slate-900">{message.name || 'Unknown'}</p>
                      <p className="mt-0.5 truncate text-xs text-slate-500">{message.email || '-'}</p>
                    </div>
                    <p className="shrink-0 text-[11px] font-semibold text-slate-400">{formatTimestamp(message.createdAt)}</p>
                  </div>
                  <p className="mt-2 line-clamp-2 text-xs text-slate-600">
                    <span className="font-semibold text-slate-700">{message.subject || '(No subject)'}:</span> {message.message || ''}
                  </p>
                </button>
              );
            })}
          </div>
        </aside>

        <div className="rounded-3xl border border-slate-200 bg-white p-4 sm:p-6">
          {!activeMessage ? (
            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
              Select a message to preview it.
            </div>
          ) : (
            <>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-slate-900">{activeMessage.subject || '(No subject)'}</p>
                  <p className="mt-1 text-xs text-slate-500">
                    From <span className="font-semibold text-slate-700">{activeMessage.name || 'Unknown'}</span> ·{' '}
                    <span className="font-semibold text-slate-700">{activeMessage.email || '-'}</span>
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => void navigator.clipboard.writeText(activeMessage.email || '')}
                    className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                  >
                    Copy email
                  </button>
                </div>
              </div>

              <div className="mt-5 flex min-h-[520px] flex-col overflow-hidden rounded-3xl border border-slate-200 bg-slate-50">
                <div className="flex-1 space-y-3 overflow-y-auto p-4">
                  <div className="flex items-start gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-white text-xs font-bold text-orange-700 shadow-sm">
                      {(activeMessage.name || 'U').trim().charAt(0).toUpperCase()}
                    </div>
                    <div className="max-w-[760px] rounded-3xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-800 shadow-sm">
                      {activeMessage.message || '(No message)'}
                      <p className="mt-2 text-[11px] font-semibold text-slate-400">{formatTimestamp(activeMessage.createdAt)}</p>
                    </div>
                  </div>

                  {activeReplies.map((reply) => (
                    <div key={reply.id} className="flex items-start justify-end gap-3">
                      <div className="max-w-[760px] rounded-3xl bg-orange-600 px-4 py-3 text-sm text-white shadow-sm">
                        {reply.body}
                        <p className="mt-2 text-right text-[11px] font-semibold text-orange-100">{formatTimestamp(reply.createdAt)}</p>
                      </div>
                      <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-orange-600 text-xs font-bold text-white shadow-sm">
                        A
                      </div>
                    </div>
                  ))}
                </div>

                <div className="border-t border-slate-200 bg-white p-3">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
                    <textarea
                      value={replyDraft}
                      onChange={(event) => setReplyDraft(event.target.value)}
                      placeholder="Type a reply..."
                      className="min-h-[44px] w-full resize-none rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-orange-400 focus:ring-2 focus:ring-orange-200/70"
                    />
                    <button
                      type="button"
                      onClick={handleSendReply}
                      disabled={!replyDraft.trim() || isReplySending}
                      className={`inline-flex items-center justify-center gap-2 rounded-2xl px-4 py-3 text-sm font-semibold text-white transition ${
                        replyDraft.trim() && !isReplySending ? 'bg-orange-600 hover:bg-orange-700' : 'cursor-not-allowed bg-slate-300'
                      }`}
                    >
                      <Send className="h-4 w-4" />
                      {isReplySending ? 'Sending...' : 'Send'}
                    </button>
                  </div>
                  {replyFeedback ? (
                    <p className="mt-2 text-[11px] font-semibold text-slate-500">{replyFeedback}</p>
                  ) : (
                    <p className="mt-2 text-[11px] font-semibold text-slate-400">
                      Sends a reply to <span className="text-slate-600">{activeMessage.email || '-'}</span> via Notifications.
                    </p>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
}
